import { forwardRef, useContext, useEffect, useState } from 'react'
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, Zoom } from '@mui/material'

import styles from './ProfileReferral.module.css'
import useTrackPageView from '../../../../helpers/hooks/use-track-page-view'
import { AppContext } from '../../../../stores/App/AppStore'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import MarkdownContent from '../../../atoms/MarkdownContent/MarkdownContent'
import { useReferralLandingPageRepository } from '../../../../repositories/ReferralLandingPage/use-referral-landing-page.repository'
import { UserContext } from '../../../../stores/User/UserStore'
import { useTranslation } from 'react-i18next'
import Spacer from '../../../atoms/Spacer/Spacer'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { logAction } from '../../../../clients/logger'
import { profileReferralPageCopyCodeMessageTemplate } from '../../../../helpers/logging/templates'
import { TransitionProps } from '@mui/material/transitions'
import GtmEventName from '../../../../helpers/analytics/GtmEventName'
import useGoogleTagManager from '../../../../helpers/analytics/use-google-tag-manager'
import { Route } from '../../../../router'
import { useNavigate } from 'react-router-dom'
import ReferralRepeatableContent from '../../../molecules/ReferralRepeatableContent/ReferralRepeatableContent'
import SuccessfulReferralCardContent from '../../../molecules/SuccessfulReferralCardContent/SuccessfulReferralCardContent'
import SplashScreenGuard from '../../../../router/guards/SplashScreenGuard'

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<string, string>;
    },
    ref: React.Ref<unknown>,
) {
    return <Zoom ref={ref} {...props} />
})

const ProfileReferral = () => {
    useTrackPageView(`Profile Referral`)

    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)
    const { getReferralPage } = useReferralLandingPageRepository()
    const { trackButtonClick } = useGoogleTagManager()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [isVisible, setIsVisible] = useState(false)

    const handleClose = () => setIsVisible(false)
    const handleShow = () => setIsVisible(true)
    const referredUsers = userState.user?.data?.referredUsers

    useEffect(() => {
        getReferralPage()
        window.scrollTo(0, 0)
    }, [])

    const handleCopy = async () => {
        try {
            if(userState.user?.data?.referralCode){
                await navigator.clipboard.writeText(userState.user?.data?.referralCode)
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: `Referral Code`,
                            text: userState.user?.data?.referralCode,
                        })
                        trackButtonClick(GtmEventName.REFERRAL_CODE_COPY_CLICK, userState.user?.data?.referralCode)
                        logAction(`debug`, profileReferralPageCopyCodeMessageTemplate(userState.user?.data?.referralCode))
                    } catch (err) {
                        logAction(`error`, `userID ${userState.user?.data?.id} encountered error while copying referral code: ${err}`)
                    }
                } else {
                    alert(`Sharing not supported on this device`)
                }
            }
        } catch (err) {
            console.error(`Failed to copy: `, err)
        }
    }

    const handleShare = async () => {
        try {
            if(userState.user?.data?.referralCode){
                await navigator.clipboard.writeText(`${process.env.REACT_APP_STARTER_DOMAIN_NAME}/referral/${userState.user?.data?.referralCode}`)
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: `Referral Code`,
                            text: `${process.env.REACT_APP_STARTER_DOMAIN_NAME}/referral/${userState.user?.data?.referralCode}`,
                        })
                        trackButtonClick(GtmEventName.REFERRAL_CODE_SHARE_CLICK, `${process.env.REACT_APP_STARTER_DOMAIN_NAME}/referral/${userState.user?.data?.referralCode}`)
                        logAction(`debug`, profileReferralPageCopyCodeMessageTemplate(`${process.env.REACT_APP_STARTER_DOMAIN_NAME}/referral/${userState.user?.data?.referralCode}`))
                    } catch (err) {
                        logAction(`error`, `userID ${userState.user?.data?.id} encountered error while sharing referral code: ${err}`)
                    }
                } else {
                    alert(`Sharing not supported on this device`)
                }
            }
        } catch (err) {
            console.error(`Failed to copy: `, err)
        }
    }

    const handleSuccessfulReferralsCardClick = () => {
        trackButtonClick(GtmEventName.PROFILE_SUCCESSFUL_REFERRAL_CARD_CLICK)
        navigate(`${Route.PROFILE_SUCCESSFUL_REFERRAL}`)
    }

    return <SplashScreenGuard>
        <Container>
            <CommonBackButton />
            <Typography className={styles.title}>{t(`profile_referral_page_title`)}</Typography>
            <Spacer height={5} />
            <Typography>{t(`profile_referral_page_description`)}</Typography>
            <Spacer height={10} />
            <Typography className={styles.referralCodeHint}>{t(`profile_referral_page_referral_hint`)}</Typography>
            <Box className={styles.referralCodeBox}>
                <Stack direction={`row`} justifyContent='space-between'>
                    <Typography>{userState.user?.data?.referralCode}</Typography>
                    <ContentCopyIcon onClick={handleCopy}/>
                </Stack>
            </Box>
            <Spacer height={15} />
            <Button variant='contained' onClick={handleShare}>
                {t(`profile_referral_page_share_button`)}
            </Button>
            <Spacer height={25} />
            <Typography className={styles.title}>{t(`profile_referral_page_referral_details_title`)}</Typography>
            <Box className={styles.pageContent}>
                <MarkdownContent content={appState.pages.referralLandingPage.description} className={styles.content}/>
                <ReferralRepeatableContent repeatableContent={appState.pages.referralLandingPage.repeatableContent ?? []}></ReferralRepeatableContent>
            </Box>
            {appState.pages.referralLandingPage.termsAndConditionsLinkText &&
            <Box>
                <Button className={styles.referralTNCs} onClick={handleShow}>
                    {appState.pages.referralLandingPage.termsAndConditionsLinkText}
                </Button>
                <Dialog open={isVisible} TransitionComponent={Transition} onClose={handleClose} className={styles.referralDialogBox} >
                    <DialogTitle className={styles.dialogTitle}>
                        <Typography className={styles.dialogTitleText}>
                            {appState.pages.referralLandingPage.termsAndConditionsHeader}
                        </Typography>
                    </DialogTitle>
                    <DialogContent >
                        <MarkdownContent content={appState.pages.referralLandingPage.termsAndConditions} />
                    </DialogContent>
                    <DialogActions>
                        <Button variant='contained' onClick={handleClose} fullWidth>{t(`close`)}</Button>
                    </DialogActions>
                </Dialog>
            </Box>
            }
            <Spacer height={10} />
            {(referredUsers?.length ?? 0) > 0 &&
            <Box className={styles.container} onClick={() => handleSuccessfulReferralsCardClick()}>
                <SuccessfulReferralCardContent referredUsers={referredUsers}/>
            </Box>
            }
        </Container>
    </SplashScreenGuard>
}

export default ProfileReferral